/**
 * Logo overlay render — uses shared FFmpeg loader.
 */

import {
  ensureFFmpeg,
  ensureScript,
  extFromName,
  getWin,
  resetFFmpeg,
} from 'tools/ffmpegShared';

export { ensureFFmpeg, hasSharedArrayBuffer, revokeUrl } from 'tools/ffmpegShared';

export type LogoAnchor = 'tl' | 'tr' | 'bl' | 'br' | 'center' | 'custom';

export type OverlayOptions = {
  anchor: LogoAnchor;
  scalePct: number;
  marginPx: number;
  xPct: number;
  yPct: number;
  videoWidth: number;
  videoHeight: number;
  onProgress?: (ratio: number) => void;
  signal?: { aborted: boolean };
};

function overlayXy(opts: OverlayOptions): { x: string; y: string } {
  const m = Math.max(0, Math.round(opts.marginPx));
  if (opts.anchor === 'custom') {
    const x = Math.round((opts.xPct / 100) * opts.videoWidth);
    const y = Math.round((opts.yPct / 100) * opts.videoHeight);
    return { x: String(x), y: String(y) };
  }
  switch (opts.anchor) {
    case 'tl':
      return { x: String(m), y: String(m) };
    case 'tr':
      return { x: `W-w-${m}`, y: String(m) };
    case 'bl':
      return { x: String(m), y: `H-h-${m}` };
    case 'br':
      return { x: `W-w-${m}`, y: `H-h-${m}` };
    case 'center':
    default:
      return { x: '(W-w)/2', y: '(H-h)/2' };
  }
}

export async function renderLogoOverlay(
  videoFile: any,
  logoFile: any,
  opts: OverlayOptions
): Promise<any> {
  const ffmpeg = await ensureFFmpeg();
  const FFmpeg = await ensureScript();
  const fetchFile = FFmpeg.fetchFile;

  ffmpeg.__onProgress = (ratio: number) => {
    if (opts.onProgress) opts.onProgress(ratio);
  };

  const vExt = extFromName(videoFile.name, 'mp4');
  const lExt = extFromName(logoFile.name, 'png');
  const inVideo = `input.${vExt}`;
  const inLogo = `logo.${lExt}`;
  const outName = 'output.mp4';

  ffmpeg.FS('writeFile', inVideo, await fetchFile(videoFile));
  ffmpeg.FS('writeFile', inLogo, await fetchFile(logoFile));

  const logoW = Math.max(8, Math.round((opts.videoWidth * opts.scalePct) / 100));
  const { x, y } = overlayXy(opts);
  const filter = `[1:v]scale=${logoW}:-1[lg];[0:v][lg]overlay=${x}:${y}:format=auto`;

  if (opts.signal && opts.signal.aborted) {
    throw new Error('Đã hủy.');
  }

  const runPromise = ffmpeg.run(
    '-i', inVideo,
    '-i', inLogo,
    '-filter_complex', filter,
    '-c:v', 'libx264',
    '-preset', 'ultrafast',
    '-crf', '23',
    '-c:a', 'aac',
    '-b:a', '128k',
    '-movflags', '+faststart',
    outName
  );

  await new Promise<void>((resolve, reject) => {
    const id = setInterval(() => {
      if (opts.signal && opts.signal.aborted) {
        clearInterval(id);
        try {
          if (ffmpeg.exit) ffmpeg.exit();
        } catch {
          /* ignore */
        }
        resetFFmpeg();
        reject(new Error('Đã hủy.'));
      }
    }, 400);
    runPromise.then(
      () => {
        clearInterval(id);
        resolve();
      },
      (err: any) => {
        clearInterval(id);
        reject(err);
      }
    );
  });

  const data = ffmpeg.FS('readFile', outName);
  try {
    ffmpeg.FS('unlink', inVideo);
    ffmpeg.FS('unlink', inLogo);
    ffmpeg.FS('unlink', outName);
  } catch {
    /* ignore */
  }

  ffmpeg.__onProgress = null;

  const win = getWin();
  const BlobCtor = win.Blob || (global as any).Blob;
  const buf = data.buffer ? data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) : data;
  return new BlobCtor([buf], { type: 'video/mp4' });
}
